import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { toast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';
import PartnerReviews from './PartnerReviews';
import { 
  Clock, 
  MapPin, 
  Phone, 
  Euro, 
  CheckCircle, 
  XCircle, 
  Calendar, 
  User, 
  Briefcase,
  TrendingUp,
  Bell,
  Star,
  Timer,
  MessageSquare,
  Search,
  Filter,
  RefreshCw,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  PlayCircle,
  PauseCircle
} from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Enhanced Job Status Badge Component
const JobStatusBadge = ({ status, t }) => {
  const statusConfig = {
    available: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: t('job.status.available', 'Disponueshme') },
    assigned: { color: 'bg-yellow-100 text-yellow-800', icon: User, label: t('job.status.assigned', 'E Caktuar') },
    in_progress: { color: 'bg-green-100 text-green-800', icon: PlayCircle, label: t('job.status.in_progress', 'Në Proces') },
    completed: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle2, label: t('job.status.completed', 'Përfunduar') },
    cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, label: t('job.status.cancelled', 'Anuluar') }
  };

  const config = statusConfig[status] || statusConfig.available;
  const Icon = config.icon;

  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// Enhanced Job Card Component
const JobCard = ({ job, isAvailable = false, onAccept, onReject, onUpdateStatus }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleStatusUpdate = async (newStatus) => {
    setIsUpdating(true);
    await onUpdateStatus(job.id, newStatus);
    setIsUpdating(false);
  };

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold text-gray-900 mb-2">
              {job.service_type?.replace('_', ' ').toUpperCase()}
            </CardTitle>
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                <span>{job.customer_name}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{job.scheduled_date}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{job.scheduled_time}</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-emerald-600 mb-1">
              €{job.estimated_price}
            </div>
            <JobStatusBadge status={job.status} />
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="flex items-center gap-1 text-gray-600 mb-3">
          <MapPin className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">{job.address}</span>
        </div>
        
        {job.special_requirements && (
          <div className="bg-blue-50 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 text-blue-800 text-sm">
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">Kërkesa Speciale:</span>
            </div>
            <p className="text-blue-700 text-sm mt-1">{job.special_requirements}</p>
          </div>
        )}

        <div className="flex justify-between items-center">
          {isAvailable ? (
            <div className="flex gap-2">
              <Button
                onClick={() => onAccept(job.id)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Prano
              </Button>
              <Button
                variant="outline"
                onClick={() => onReject(job.id)}
                className="border-red-300 text-red-600 hover:bg-red-50 px-4 py-2 text-sm"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Refuzo
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              {job.status === 'assigned' && (
                <Button
                  onClick={() => handleStatusUpdate('in_progress')}
                  disabled={isUpdating}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm"
                >
                  <PlayCircle className="h-4 w-4 mr-1" />
                  Fillo Punën
                </Button>
              )}
              {job.status === 'in_progress' && (
                <Button
                  onClick={() => handleStatusUpdate('completed')}
                  disabled={isUpdating}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Përfundo Punën
                </Button>
              )}
            </div>
          )}
          
          <div className="text-xs text-gray-500">
            {job.created_at && `Krijuar: ${format(new Date(job.created_at), 'dd/MM/yyyy')}`}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Enhanced Filter Component
const JobFilter = ({ filters, onFiltersChange }) => {
  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              <Search className="h-4 w-4 inline mr-1" />
              Kërko
            </label>
            <Input
              placeholder="Kërko sipas emrit, adresës..."
              value={filters.search}
              onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Lloji i Shërbimit
            </label>
            <Select
              value={filters.serviceType}
              onValueChange={(value) => onFiltersChange({ ...filters, serviceType: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Të gjitha" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Të gjitha</SelectItem>
                <SelectItem value="general_cleaning">Pastrimi i Përgjithshëm</SelectItem>
                <SelectItem value="apartment_cleaning">Pastrimi i Apartamentit</SelectItem>
                <SelectItem value="business_cleaning">Pastrimi i Biznesit</SelectItem>
                <SelectItem value="deep_cleaning">Pastrimi i Thellë</SelectItem>
                <SelectItem value="airbnb_management">Menaxhimi AirBNB</SelectItem>
                <SelectItem value="autodetailing">Autodetailing</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Statusi
            </label>
            <Select
              value={filters.status}
              onValueChange={(value) => onFiltersChange({ ...filters, status: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Të gjitha" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Të gjitha</SelectItem>
                <SelectItem value="available">Disponueshme</SelectItem>
                <SelectItem value="assigned">E Caktuar</SelectItem>
                <SelectItem value="in_progress">Në Proces</SelectItem>
                <SelectItem value="completed">Përfunduar</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Renditja
            </label>
            <Select
              value={filters.sortBy}
              onValueChange={(value) => onFiltersChange({ ...filters, sortBy: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Data" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">Data (të fundit)</SelectItem>
                <SelectItem value="date_asc">Data (të vjetra)</SelectItem>
                <SelectItem value="price_desc">Çmimi (të larta)</SelectItem>
                <SelectItem value="price_asc">Çmimi (të ulëta)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EnhancedPartnerDashboard = ({ partnerId, onBack }) => {
  const { t } = useLanguage();
  const [partner, setPartner] = useState(null);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('available');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Enhanced filtering states
  const [filters, setFilters] = useState({
    search: '',
    serviceType: 'all',
    status: 'all',
    sortBy: 'date_desc'
  });

  // Auto-refresh functionality
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const loadDashboardData = useCallback(async (showRefreshIndicator = true) => {
    if (showRefreshIndicator) {
      setRefreshing(true);
    }
    
    try {
      // Load partner info
      const partnerResponse = await axios.get(`${API}/partners/${partnerId}`);
      setPartner(partnerResponse.data);

      // Load available jobs for this partner
      const availableResponse = await axios.get(`${API}/partners/${partnerId}/available-jobs`);
      setAvailableJobs(availableResponse.data);

      // Load assigned jobs
      const assignedResponse = await axios.get(`${API}/partners/${partnerId}/jobs`);
      setAssignedJobs(assignedResponse.data);

      // Load notifications
      const notificationsResponse = await axios.get(`${API}/partners/${partnerId}/notifications`);
      setNotifications(notificationsResponse.data);

      // Load payouts
      const payoutsResponse = await axios.get(`${API}/partners/${partnerId}/payouts`);
      setPayouts(payoutsResponse.data);

      // Load Stripe account status if partner has Stripe account
      if (partnerResponse.data.stripe_account_id) {
        try {
          const stripeStatusResponse = await axios.get(`${API}/partners/${partnerId}/stripe/account-status`);
          setStripeAccountStatus(stripeStatusResponse.data);
        } catch (error) {
          console.log('Stripe account status not available:', error);
        }
      }

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast({
        title: "Gabim",
        description: "Nuk mund të ngarkohen të dhënat e dashboard-it.",
        variant: "destructive",
      });
    }
    setLoading(false);
    if (showRefreshIndicator) {
      setRefreshing(false);
    }
  }, [partnerId]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    let interval;
    if (autoRefresh && partnerId) {
      interval = setInterval(() => {
        loadDashboardData(false);
      }, 30000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefresh, partnerId, loadDashboardData]);

  useEffect(() => {
    if (partnerId) {
      loadDashboardData();
    }
  }, [partnerId, loadDashboardData]);

  const acceptJob = async (jobId) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/accept/${partnerId}`);
      toast({
        title: "Sukses",
        description: "Puna u pranua me sukses!",
      });
      loadDashboardData();
    } catch (error) {
      toast({
        title: "Gabim",
        description: "Nuk mund të pranohej puna.",
        variant: "destructive",
      });
    }
  };

  const rejectJob = async (jobId) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/reject/${partnerId}`);
      toast({
        title: "Sukses",
        description: "Puna u refuzua.",
      });
      loadDashboardData();
    } catch (error) {
      toast({
        title: "Gabim",
        description: "Nuk mund të refuzohej puna.",
        variant: "destructive",
      });
    }
  };

  const updateJobStatus = async (jobId, status) => {
    try {
      await axios.put(`${API}/jobs/${jobId}/status`, null, {
        params: { status, partner_id: partnerId }
      });
      toast({
        title: "Sukses",
        description: "Statusi i punës u përditësua!",
      });
      loadDashboardData();
    } catch (error) {
      toast({
        title: t('dashboard.error', 'Gabim'),
        description: t('job.updateError', 'Nuk mund të përditësohej statusi.'),
        variant: "destructive",
      });
    }
  };

  // Filter jobs based on current filters
  const filterJobs = (jobs) => {
    return jobs.filter(job => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          job.customer_name?.toLowerCase().includes(searchLower) ||
          job.address?.toLowerCase().includes(searchLower) ||
          job.service_type?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Service type filter
      if (filters.serviceType !== 'all' && job.service_type !== filters.serviceType) {
        return false;
      }

      // Status filter
      if (filters.status !== 'all' && job.status !== filters.status) {
        return false;
      }

      return true;
    }).sort((a, b) => {
      // Sorting
      switch (filters.sortBy) {
        case 'date_desc':
          return new Date(b.scheduled_date) - new Date(a.scheduled_date);
        case 'date_asc':
          return new Date(a.scheduled_date) - new Date(b.scheduled_date);
        case 'price_desc':
          return (b.estimated_price || 0) - (a.estimated_price || 0);
        case 'price_asc':
          return (a.estimated_price || 0) - (b.estimated_price || 0);
        default:
          return 0;
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-emerald-600" />
          <p className="text-gray-600">{t('dashboard.loading', 'Duke ngarkuar dashboard-in...')}</p>
        </div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 mx-auto mb-4 text-red-600" />
          <p className="text-gray-600">{t('dashboard.error', 'Partner nuk u gjet.')}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'available', label: t('dashboard.tabs.available', 'Punë të Disponueshme'), count: availableJobs.length },
    { key: 'assigned', label: t('dashboard.tabs.assigned', 'Punët e Mia'), count: assignedJobs.length },
    { key: 'notifications', label: t('dashboard.tabs.notifications', 'Njoftimet'), count: notifications.length },
    { key: 'payouts', label: t('dashboard.tabs.payouts', 'Pagesat'), count: payouts.length },
    { key: 'earnings', label: t('dashboard.tabs.earnings', 'Të Ardhurat'), count: null },
    { key: 'reviews', label: t('dashboard.tabs.reviews', 'Vlerësimet'), count: null }
  ];

  const filteredAvailableJobs = filterJobs(availableJobs);
  const filteredAssignedJobs = filterJobs(assignedJobs);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost"
                onClick={onBack}
                className="text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t('dashboard.backBtn', 'Kthehu')}
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {t('dashboard.welcome', 'Përshëndetje, {name}!').replace('{name}', partner.firstName)}
                </h1>
                <p className="text-gray-600">{t('dashboard.title', 'Dashboard i Partnerit - Brillanz & Glanz')}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right text-sm">
                <div className="text-gray-500">{t('dashboard.lastUpdate', 'Përditësuar:')}</div>
                <div className="text-gray-800">{format(lastRefresh, 'HH:mm:ss')}</div>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadDashboardData()}
                disabled={refreshing}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                {t('dashboard.refresh', 'Rifresko')}
              </Button>
              
              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className="flex items-center gap-2"
              >
                <Timer className="h-4 w-4" />
                {autoRefresh ? t('dashboard.autoOn', 'Auto ON') : t('dashboard.autoOff', 'Auto OFF')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Partner Stats */}
      <div className="container mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Euro className="h-8 w-8 text-emerald-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t('stats.totalEarnings', 'Të Ardhurat Totale')}</p>
                  <p className="text-2xl font-bold">€{partner.totalEarnings || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Briefcase className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t('stats.completedJobs', 'Punë të Përfunduara')}</p>
                  <p className="text-2xl font-bold">{partner.totalJobs || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Star className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t('stats.rating', 'Vlerësimi')}</p>
                  <p className="text-2xl font-bold">{partner.rating || 0}⭐</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Bell className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t('stats.newNotifications', 'Njoftimet e Reja')}</p>
                  <p className="text-2xl font-bold">{notifications.filter(n => !n.read).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.count !== null && (
                  <Badge className="ml-2 bg-gray-100 text-gray-600">{tab.count}</Badge>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {/* Available Jobs Tab */}
          {activeTab === 'available' && (
            <div>
              <JobFilter filters={filters} onFiltersChange={setFilters} />
              
              <div className="mb-4 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('dashboard.tabs.available', 'Punë të Disponueshme')} ({filteredAvailableJobs.length})
                </h2>
              </div>
              
              {filteredAvailableJobs.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-8">
                    <Briefcase className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {t('empty.availableJobs', 'Nuk ka punë të disponueshme')}
                    </h3>
                    <p className="text-gray-500">
                      {t('empty.availableJobsDesc', 'Kur të ketë punë të reja që përputhen me specializimin tuaj, do të shfaqen këtu.')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {filteredAvailableJobs.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      isAvailable={true}
                      onAccept={acceptJob}
                      onReject={rejectJob}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Assigned Jobs Tab */}
          {activeTab === 'assigned' && (
            <div>
              <JobFilter filters={filters} onFiltersChange={setFilters} />
              
              <div className="mb-4 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">
                  Punët e Mia ({filteredAssignedJobs.length})
                </h2>
              </div>
              
              {filteredAssignedJobs.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-8">
                    <CheckCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Nuk keni punë të caktuara
                    </h3>
                    <p className="text-gray-500">
                      Punët që i pranoni do të shfaqen këtu.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {filteredAssignedJobs.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      isAvailable={false}
                      onUpdateStatus={updateJobStatus}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Other tabs remain the same as original PartnerDashboard */}
          {activeTab === 'notifications' && (
            <Card>
              <CardHeader>
                <CardTitle>Njoftimet</CardTitle>
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">Nuk ka njoftimet e reja.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {notifications.map((notification, index) => (
                      <div key={index} className="border-b pb-3 last:border-b-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium">{notification.type}</p>
                            <p className="text-sm text-gray-600">{notification.message}</p>
                          </div>
                          <span className="text-xs text-gray-500">
                            {format(new Date(notification.created_at), 'dd/MM/yyyy HH:mm')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Earnings and other tabs would be implemented similarly */}
          {activeTab === 'earnings' && (
            <Card>
              <CardHeader>
                <CardTitle>Të Ardhurat</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <TrendingUp className="h-12 w-12 mx-auto text-emerald-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900">€{partner.totalEarnings || 0}</h3>
                  <p className="text-gray-600">Të ardhurat totale</p>
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div className="bg-emerald-50 rounded-lg p-4">
                      <div className="text-emerald-600 font-semibold">Këtë muaj</div>
                      <div className="text-2xl font-bold">€0</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-blue-600 font-semibold">Mesatarja</div>
                      <div className="text-2xl font-bold">€{Math.round((partner.totalEarnings || 0) / Math.max(partner.totalJobs || 1, 1))}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedPartnerDashboard;